'use client';

import { useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

interface PrimaryHeroProps {
  eyebrow?: React.ReactNode;
  headline: React.ReactNode;
  subheadline?: React.ReactNode;
  cta?: React.ReactNode;
  backgroundMedia?: React.ReactNode;
  className?: string;
  variant?: 'default' | 'dancer' | 'organizer';
  showScrollIndicator?: boolean;
}

export function PrimaryHero({
  eyebrow,
  headline,
  subheadline,
  cta,
  backgroundMedia,
  className,
  variant = 'default',
  showScrollIndicator = false,
}: PrimaryHeroProps) {
  const heroRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 },
    );
    const elements = heroRef.current?.querySelectorAll('.scroll-fade-up');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const variantColors = {
    default: 'from-primary',
    dancer: 'from-purple-600 dark:from-purple-500',
    organizer: 'from-primary',
  };

  return (
    <section
      ref={heroRef}
      className={cn(
        'relative isolate -mt-20 flex min-h-dvh items-center justify-center overflow-hidden pt-20',
        'from-muted to-background bg-gradient-to-b',
        className,
      )}
    >
      {/* Background Media */}
      {backgroundMedia && (
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/85 via-black/70 to-black/85" />
          {backgroundMedia}
        </div>
      )}
      {/* Content Container */}
      <div className="relative z-20 container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-5xl space-y-12 py-24 text-center">
          {/* Eyebrow */}
          {eyebrow && (
            <div className="scroll-fade-up stagger-1 mb-6">
              <span
                className={cn(
                  'inline-block text-sm font-semibold tracking-wider uppercase md:text-base',
                  backgroundMedia ? 'text-white' : 'text-muted-foreground',
                )}
              >
                {eyebrow}
              </span>
            </div>
          )}
          {/* Headline */}
          <h1
            className={cn(
              'scroll-fade-up stagger-2 mb-6',
              'text-4xl md:text-6xl lg:text-7xl xl:text-8xl',
              'leading-[0.9] font-bold tracking-[-0.02em]',
              'antialiased',
              backgroundMedia ? 'text-white' : 'text-foreground',
            )}
          >
            {backgroundMedia ? (
              headline
            ) : (
              <span
                className={cn(
                  'block bg-gradient-to-r',
                  `${variantColors[variant]} to-foreground`,
                  'bg-clip-text text-transparent',
                )}
              >
                {headline}
              </span>
            )}
          </h1>
          {/* Subheadline */}
          {subheadline && (
            <p
              className={cn(
                'scroll-fade-up stagger-3 mx-auto mb-10 max-w-3xl',
                'text-lg leading-[1.4] font-normal tracking-[-0.01em] md:text-xl lg:text-2xl',
                backgroundMedia ? 'text-white' : 'text-muted-foreground',
              )}
            >
              {subheadline}
            </p>
          )}
          {/* CTA */}
          {cta && <div className="scroll-fade-up stagger-4 pt-8">{cta}</div>}
        </div>
      </div>
      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <div className="absolute bottom-8 left-1/2 z-30 -translate-x-1/2 transform">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
            }}
            className={cn(
              'rounded-full p-2 transition-all duration-300 hover:scale-110',
              'animate-bounce cursor-pointer',
              backgroundMedia
                ? 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30'
                : 'bg-primary/10 text-primary hover:bg-primary/20',
            )}
            aria-label={t('marketing:hero.scrollToContent')}
          >
            <ChevronDown className="h-6 w-6" />
          </Button>
        </div>
      )}
    </section>
  );
}
